import axios from "axios";
import { API_ROUTES } from "@/constants/apiRoutes";
import {
  getStoredAccessToken,
  getStoredRefreshToken,
  storeRotatedRefreshToken,
  storeTokenResponse,
} from "@/services/authTokenStorage";
import { FORM_HTTP_HEADERS } from "@/services/httpHeaders";
import {
  createRefreshGrantPayload,
  NO_PROJECT_TOKEN_SCOPE,
  projectTokenScope,
} from "@/services/iamPayloads";
import type { TokenResponse } from "@/types";

let refreshPromise: Promise<string | null> | null = null;
let pendingQueue: Array<(token: string | null) => void> = [];

function waitForRefresh(): Promise<string | null> {
  return new Promise((resolve) => {
    pendingQueue.push(resolve);
  });
}

export async function refreshStoredAccessToken(): Promise<string | null> {
  if (refreshPromise) return waitForRefresh();

  const accessToken = getStoredAccessToken();
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  refreshPromise = (async () => {
    let refreshedToken: string | null = null;

    try {
      const { data } = await axios.post<TokenResponse>(
        API_ROUTES.iam.token,
        createRefreshGrantPayload(refreshToken),
        { headers: FORM_HTTP_HEADERS },
      );
      // Logout or a new login must win over an obsolete refresh response.
      if (getStoredAccessToken() !== accessToken
        || getStoredRefreshToken() !== refreshToken) return null;
      refreshedToken = storeTokenResponse(data);
      return refreshedToken;
    } catch {
      return null;
    } finally {
      const queued = pendingQueue;
      pendingQueue = [];
      refreshPromise = null;
      queued.forEach((resolve) => resolve(refreshedToken));
    }
  })();

  return refreshPromise;
}

export async function refreshAccessTokenForProject(
  projectId: string | null,
): Promise<string | null> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    const { data } = await axios.post<TokenResponse>(
      API_ROUTES.iam.token,
      createRefreshGrantPayload(
        refreshToken,
        projectId ? projectTokenScope(projectId) : NO_PROJECT_TOKEN_SCOPE,
      ),
      { headers: FORM_HTTP_HEADERS },
    );
    return storeTokenResponse(data);
  } catch {
    return null;
  }
}

/**
 * An access token for another project, leaving the session where it is.
 *
 * A component is written into the project the token of the request is scoped
 * to, so handing one over to another project needs a token that project
 * answers for. Moving the session onto it instead would point every other
 * request at the wrong project for as long as the write took, and leave the
 * session stranded there if the way back ever failed -- which is what a delete
 * pinned to the visitor's own project would then be refused for.
 */
export async function requestProjectAccessToken(
  projectId: string,
): Promise<string | null> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    const { data } = await axios.post<TokenResponse>(
      API_ROUTES.iam.token,
      createRefreshGrantPayload(refreshToken, projectTokenScope(projectId)),
      { headers: FORM_HTTP_HEADERS },
    );
    return storeRotatedRefreshToken(data);
  } catch {
    return null;
  }
}
